import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { IconComponent } from '../../../shared-with-client/icon/icon.component';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { TextInputComponent } from '../../../shared-with-client/text-input/text-input.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StoresService } from '../../../core/services/stores.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TeyaService, ITeyaPaymentStatus } from '../../../core/services/teya.service';
import { MerchantCurrencyPipe } from '../../pipes/merchant-currency.pipe';
import { Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { RouterLink } from '@angular/router';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 10 * 60 * 1000;
const FINAL_PAYMENT_STATUSES = ['SUCCESSFUL', 'FAILED', 'CANCELLED'];
const SUCCESSFUL_PAYMENT_STATUS = 'SUCCESSFUL';
@Component({
    selector: 'app-simple-payment-modal',
    imports: [LoaderComponent, IconComponent, ButtonComponent, TextInputComponent, ReactiveFormsModule, TranslatePipe, MerchantCurrencyPipe, RouterLink],
    templateUrl: './simple-payment-modal.component.html',
    styleUrl: './simple-payment-modal.component.scss',
})
export class SimplePaymentModalComponent implements OnInit {
    private storesService = inject(StoresService);
    private formBuilder = inject(FormBuilder);
    public currentStore = toSignal(this.storesService.getActiveStore());
    public dialogRef = inject(DialogRef<any>);
    private teyaService = inject(TeyaService);
    private translateService = inject(TranslateService);
    private destroyRef = inject(DestroyRef);

    public data = inject(DIALOG_DATA) as {
        bookingId?: any;
        amount?: number;
    };

    public submitAttempted = signal<any>(false);
    public form!: FormGroup;

    public loading = signal<boolean>(false);
    public saving = signal<boolean>(false);
    public success = signal<boolean>(false);
    public failed = signal<boolean>(false);
    public errorMessage = signal<string | null>(null);
    public cancellingTeyaPayment = signal(false);

    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());

    private stopPolling$ = new Subject<void>();
    private activeSaleId: number | null = null;
    private activePaymentRequestId: string | null = null;

    ngOnInit(): void {
        const minAmount = this.data.bookingId ? 0 : 0.01;
        this.form = this.formBuilder.group({
            amount: [
                this.data.amount ? Math.max(this.data.amount / 100, 0) : null,
                [Validators.required, Validators.min(minAmount)],
            ],
        });
    }

    sendToTerminal(): void {
        this.failed.set(false);
        this.success.set(false);
        this.errorMessage.set(null);
        this.saving.set(true);
        const amountCents = Math.round(Number(this.form.value.amount) * 100);
        const storeId = this.currentStore()?.id;

        const request$ = this.data.bookingId
            ? this.teyaService.initiateBookingPayment(storeId, this.data.bookingId, amountCents)
            : this.teyaService.initiateSoloPayment(storeId, amountCents);

        request$.subscribe({
            next: (result) => {
                this.activeSaleId = result.saleId;
                this.activePaymentRequestId = result.paymentRequestId;
                this.pollPaymentStatus(result.saleId, result.paymentRequestId);
            },
            error: (error) => {
                this.markPaymentFailed(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'));
            },
        });
    }

    getPaymentStatus(saleId: number, paymentRequestId: string) {
        return this.teyaService.getPaymentStatus(saleId, paymentRequestId);
    }

    private pollPaymentStatus(saleId: number, paymentRequestId: string): void {
        let reachedFinalStatus = false;
        this.failed.set(false);
        this.errorMessage.set(null);

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.getPaymentStatus(saleId, paymentRequestId)),
                takeWhile((status) => !this.isFinalPaymentStatus(status), true),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (status: ITeyaPaymentStatus) => {
                    if (this.isFinalPaymentStatus(status)) {
                        reachedFinalStatus = true;
                        this.saving.set(false);
                        this.success.set(this.isSuccessfulPaymentStatus(status));
                        this.activeSaleId = null;
                        this.activePaymentRequestId = null;
                        if (!this.isSuccessfulPaymentStatus(status)) {
                            this.markPaymentFailed(
                                this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
                            );
                        }
                    }
                },
                error: (error) => {
                    this.markPaymentFailed(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'));
                },
                complete: () => {
                    if (!reachedFinalStatus && this.saving()) {
                        this.markPaymentFailed(this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentTimeout'));
                    }
                },
            });
    }

    private isFinalPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return FINAL_PAYMENT_STATUSES.includes(status.status);
    }

    private isSuccessfulPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return status.status === SUCCESSFUL_PAYMENT_STATUS;
    }

    submit() {
        this.submitAttempted.set(true);
        if (this.form.invalid) {
            return;
        }
        this.sendToTerminal();
    }

    cancelWaitingPayment() {
        const saleId = this.activeSaleId;
        const paymentRequestId = this.activePaymentRequestId;

        if (!saleId || !paymentRequestId || this.cancellingTeyaPayment()) {
            this.finishLocalCancelAsFailed();
            return;
        }

        this.cancellingTeyaPayment.set(true);
        this.teyaService.cancelPayment(saleId, paymentRequestId).subscribe({
            next: (status) => {
                this.cancellingTeyaPayment.set(false);
                this.handleCancelOutcome(saleId, paymentRequestId, status);
            },
            error: (error) => {
                this.cancellingTeyaPayment.set(false);

                if (this.isAlreadyFinishedCancelError(error)) {
                    this.reconcileAfterCancelConflict(saleId, paymentRequestId);
                    return;
                }

                this.finishLocalCancelAsFailed(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                );
            },
        });
    }

    private handleCancelOutcome(saleId: number, paymentRequestId: string, status: ITeyaPaymentStatus) {
        if (this.isSuccessfulPaymentStatus(status)) {
            this.finishLocalCancelAsPaid();
            return;
        }

        this.finishLocalCancelAsFailed();
    }

    private reconcileAfterCancelConflict(saleId: number, paymentRequestId: string) {
        this.teyaService.getPaymentStatus(saleId, paymentRequestId, true).subscribe({
            next: (status) => {
                this.handleCancelOutcome(saleId, paymentRequestId, status);
            },
            error: (error) => {
                this.finishLocalCancelAsFailed(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                );
            },
        });
    }

    private isAlreadyFinishedCancelError(error: any): boolean {
        if (error?.errData?.code === 'TEYA_PAYMENT_ALREADY_FINISHED') {
            return true;
        }

        const message = String(error?.errMsg || error?.message || '').toLowerCase();

        return message.includes('already finished');
    }

    private finishLocalCancelAsPaid() {
        this.stopPolling$.next();
        this.saving.set(false);
        this.failed.set(false);
        this.errorMessage.set(null);
        this.success.set(true);
        this.activeSaleId = null;
        this.activePaymentRequestId = null;
    }

    private finishLocalCancelAsFailed(message?: string) {
        this.stopPolling$.next();
        this.markPaymentFailed(
            message || this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
        );
        this.activeSaleId = null;
        this.activePaymentRequestId = null;
    }

    private markPaymentFailed(message: string) {
        this.saving.set(false);
        this.success.set(false);
        this.failed.set(true);
        this.errorMessage.set(message);
    }

    cancelFailure(): void {
        this.failed.set(false);
        this.errorMessage.set(null);
    }

    close(reason?: string) {
        this.dialogRef.close(reason);
    }
}
